import { Table, Button, Tag, Card, Space, Spin, Empty } from 'antd';
import { DownloadOutlined, RocketOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { agentVersionsApi } from '../api/agents';
import type { AgentVersion } from '../types';

export function AgentDownloads() {
  const { data: versions = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['agent-versions'],
    queryFn: agentVersionsApi.getAll,
  });

  const { data: latestVersion } = useQuery({
    queryKey: ['agent-versions', 'latest'],
    queryFn: agentVersionsApi.getLatest,
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const columns = [
    {
      title: 'Versiyon',
      dataIndex: 'version',
      key: 'version',
      render: (version: string, record: AgentVersion) => (
        <Space>
          <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
            v{version}
          </Tag>
          {latestVersion?.id === record.id && (
            <Tag color="green">En Guncel</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Boyut',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size: number) => formatFileSize(size),
    },
    {
      title: 'Yayin Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
    },
    {
      title: 'Indir',
      key: 'download',
      render: (_: unknown, record: AgentVersion) => (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          href={record.downloadUrl}
          target="_blank"
        >
          Indir
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Agent Indirmeleri</h2>
        <Button
          icon={<ReloadOutlined spin={isFetching} />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Yenile
        </Button>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {latestVersion && (
            <Card style={{ marginBottom: 16, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <Space align="start">
                <RocketOutlined style={{ fontSize: 48, color: 'white' }} />
                <div style={{ color: 'white' }}>
                  <h3 style={{ color: 'white', margin: 0 }}>RaporGor Agent v{latestVersion.version}</h3>
                  <p style={{ margin: '8px 0', opacity: 0.9 }}>
                    En guncel versiyon {'\u2022'} {formatFileSize(latestVersion.fileSize)}
                  </p>
                  <Button
                    type="default"
                    size="large"
                    icon={<DownloadOutlined />}
                    href={latestVersion.downloadUrl}
                    target="_blank"
                    style={{ marginTop: 8 }}
                  >
                    Simdi Indir
                  </Button>
                </div>
              </Space>
            </Card>
          )}

          {versions.length > 0 ? (
            <>
              <h3 style={{ marginTop: 24, marginBottom: 16 }}>Tum Versiyonlar</h3>
              <Table
                columns={columns}
                dataSource={versions}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </>
          ) : (
            <Empty
              description="Henuz agent versiyonu yuklenmemis"
              style={{ marginTop: 50 }}
            />
          )}
        </>
      )}
    </div>
  );
}
